import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { Clock, Scissors, Plus, Zap, Film, Music, Mic } from 'lucide-react';
import { Button } from '../../ui/button';

import { PX_PER_SEC, LABEL_W, RULER_H, VIDEO_H, AUDIO_H, LAYER_H, TRANSITION_LABELS, getEffDur } from './timeline/constants';
import { SortableClip } from './timeline/Clip';
import { AudioBlock } from './timeline/AudioBlock';
import { LayerBlock } from './timeline/LayerBlock';
import { ClipEditor } from './timeline/ClipEditor';
import { AudioEditor } from './timeline/AudioEditor';

/* ── Drop Zones ── */

const TimelineDropZone = ({ isOver, onAddBlank }) => {
  const { setNodeRef } = useDroppable({ id: 'timeline-add-zone' });
  return (
    <div ref={setNodeRef} className={`flex-shrink-0 rounded border-2 border-dashed flex items-center justify-center transition-all cursor-pointer ${
      isOver ? 'border-green-400 bg-green-400/10 text-green-400' : 'border-border/30 text-muted-foreground hover:border-purple-400 hover:text-purple-400'
    }`} style={{ width: 40, height: VIDEO_H }} data-testid="timeline-add-zone" onClick={onAddBlank}>
      <Plus className="h-3.5 w-3.5" />
    </div>
  );
};

const DroppableClipWrapper = ({ clipIndex, children }) => {
  const { setNodeRef } = useDroppable({ id: `timeline-clip-${clipIndex}`, data: { type: 'timeline-clip', index: clipIndex } });
  return <div ref={setNodeRef}>{children}</div>;
};

/* ═══════════════════════════════════════════════════════════
   TIMELINE — Multi-Track Pro Sequencer (Refactored)
   ═══════════════════════════════════════════════════════════ */

export const Timeline = ({
  tracks, onTracksChange, activeDropTarget, currentTime = 0, onSeek, onAddBlank,
  audioSegments = [], onAudioSegmentsChange, isPlaying = false,
  layers = [], selectedLayerId = null, onSelectLayer, onLayerUpdate,
}) => {
  const [selectedTrackIdx, setSelectedTrackIdx] = useState(null);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [draggingAudioId, setDraggingAudioId] = useState(null);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const audioStartRef = useRef(null);
  const playheadRef = useRef(null);

  const videoEndTime = tracks.reduce((sum, t) => sum + getEffDur(t), 0);
  const audioEndTime = audioSegments.length > 0
    ? Math.max(...audioSegments.map(a => (a.start_time || 0) + (a.duration || 0)))
    : 0;
  const totalDuration = Math.max(videoEndTime, audioEndTime, 0.5);
  const totalWidth = totalDuration * PX_PER_SEC + 80;

  const musicSegs = audioSegments.filter(s => s.type === 'music');
  const voSegs = audioSegments.filter(s => s.type === 'voiceover');
  const sortableIds = tracks.map((t, i) => `track-${t.asset_id}-${i}`);

  const updateTrack = (index, updates) => onTracksChange(tracks.map((t, i) => i === index ? { ...t, ...updates } : t));
  const removeTrack = (index) => { onTracksChange(tracks.filter((_, i) => i !== index)); setSelectedTrackIdx(null); };
  const moveTrack = (index, dir) => {
    const ni = dir === 'left' ? index - 1 : index + 1;
    if (ni < 0 || ni >= tracks.length) return;
    const arr = [...tracks]; [arr[index], arr[ni]] = [arr[ni], arr[index]];
    onTracksChange(arr); setSelectedTrackIdx(ni);
  };

  const updateAudioSeg = (id, updates) => {
    if (onAudioSegmentsChange) onAudioSegmentsChange(audioSegments.map(a => a.id === id ? { ...a, ...updates } : a));
  };
  const removeAudioSeg = (id) => {
    if (onAudioSegmentsChange) onAudioSegmentsChange(audioSegments.filter(a => a.id !== id));
    setSelectedAudioId(null);
  };

  /* ── Split (video or audio) ── */
  const splitAtPlayhead = () => {
    if (selectedTrackIdx !== null && tracks[selectedTrackIdx]) {
      let acc = 0;
      for (let i = 0; i < selectedTrackIdx; i++) acc += getEffDur(tracks[i]);
      const clipEff = getEffDur(tracks[selectedTrackIdx]);
      const rel = currentTime - acc;
      if (rel <= 0.2 || rel >= clipEff - 0.2) { toast.error('Playhead too close to clip edge'); return; }
      const t = tracks[selectedTrackIdx];
      const sp = t.speed_factor || 1;
      const splitDur = rel * sp;
      const c1 = { ...t, duration: parseFloat(splitDur.toFixed(2)) };
      const c2 = { ...t, duration: parseFloat(((t.duration || 3) - splitDur).toFixed(2)), transition: 'none' };
      if (t.type === 'video') { c1.trim_end = (t.trim_start || 0) + splitDur; c2.trim_start = c1.trim_end; }
      onTracksChange([...tracks.slice(0, selectedTrackIdx), c1, c2, ...tracks.slice(selectedTrackIdx + 1)]);
      toast.success('Video clip split'); return;
    }
    if (selectedAudioId && onAudioSegmentsChange) {
      const seg = audioSegments.find(a => a.id === selectedAudioId);
      if (!seg) return;
      const rel = currentTime - (seg.start_time || 0);
      if (rel <= 0.2 || rel >= (seg.duration || 1) - 0.2) { toast.error('Playhead too close to segment edge'); return; }
      const s1 = { ...seg, duration: parseFloat(rel.toFixed(2)), id: seg.id };
      const s2 = { ...seg, id: `${seg.id}_b`, start_time: parseFloat(((seg.start_time || 0) + rel).toFixed(2)), trim_start: (seg.trim_start || 0) + rel, duration: parseFloat(((seg.duration || 1) - rel).toFixed(2)) };
      onAudioSegmentsChange(audioSegments.map(a => a.id === seg.id ? s1 : a).concat(s2));
      toast.success('Audio segment split'); return;
    }
    toast.error('Select a clip or audio segment first');
  };

  /* ── Time ↔ Pixel ── */
  const getTimeFromX = useCallback((clientX) => {
    if (!contentRef.current || !scrollRef.current) return 0;
    const rect = contentRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(totalDuration, (clientX - rect.left) / PX_PER_SEC));
  }, [totalDuration]);

  const cursorPx = Math.max(0, Math.min(totalWidth, currentTime * PX_PER_SEC));

  /* ── Smooth playhead: direct DOM update bypasses React re-render lag ── */
  useEffect(() => {
    if (playheadRef.current) {
      playheadRef.current.style.left = `${cursorPx}px`;
    }
  }, [cursorPx]);

  /* ── Auto-scroll to keep playhead visible during playback ── */
  useEffect(() => {
    if (!isPlaying || !scrollRef.current) return;
    const container = scrollRef.current;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    const margin = 80;
    if (cursorPx > viewRight - margin) {
      container.scrollLeft = cursorPx - container.clientWidth + margin;
    } else if (cursorPx < viewLeft + margin) {
      container.scrollLeft = Math.max(0, cursorPx - margin);
    }
  }, [cursorPx, isPlaying]);

  /* ── Draggable Playhead ── */
  useEffect(() => {
    if (!isDraggingPlayhead) return;
    const onMove = (e) => { if (onSeek) onSeek(getTimeFromX(e.clientX)); };
    const onUp = () => setIsDraggingPlayhead(false);
    // Use pointer events so the same code path handles mouse + touch + stylus.
    // Without this, touch drags on mobile fall through to the browser's default
    // page-pan behaviour and the canvas appears to drag around the editor.
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDraggingPlayhead, getTimeFromX, onSeek]);

  /* ── Draggable Audio Segments ── */
  useEffect(() => {
    if (!draggingAudioId) return;
    const onMove = (e) => {
      const d = audioStartRef.current;
      if (!d) return;
      const newStart = Math.max(0, d.startTime + (e.clientX - d.mouseX) / PX_PER_SEC);
      updateAudioSeg(draggingAudioId, { start_time: parseFloat(newStart.toFixed(2)) });
    };
    const onUp = () => setDraggingAudioId(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [draggingAudioId]);

  const handleAudioDragStart = (e, seg) => {
    e.preventDefault(); e.stopPropagation();
    setDraggingAudioId(seg.id);
    setSelectedAudioId(seg.id);
    setSelectedTrackIdx(null);
    audioStartRef.current = { startTime: seg.start_time || 0, mouseX: e.clientX };
  };

  const handleRulerClick = (e) => { if (onSeek) onSeek(getTimeFromX(e.clientX)); };
  const handleSelectTrack = (idx) => { setSelectedTrackIdx(idx); setSelectedAudioId(null); };
  const handleSelectAudio = (id) => { setSelectedAudioId(id); setSelectedTrackIdx(null); };

  const selected = selectedTrackIdx !== null ? tracks[selectedTrackIdx] : null;
  const selectedAudio = selectedAudioId ? audioSegments.find(a => a.id === selectedAudioId) : null;
  const hasSelection = selected || selectedAudio;

  /* ── Tick marks ── */
  const ticks = [];
  for (let s = 0; s <= totalDuration; s++) {
    ticks.push(
      <div key={s} className="absolute bottom-0 flex flex-col items-center" style={{ left: s * PX_PER_SEC }}>
        <span className="text-[7px] text-muted-foreground leading-none mb-0.5">{s}s</span>
        <div className="w-px h-1.5 bg-border/50" />
      </div>
    );
  }

  const showAudioTracks = musicSegs.length > 0 || audioSegments.length > 0 || tracks.length > 0;

  return (
    <div className="border border-border/50 rounded-lg bg-card" data-testid="reel-timeline">
      {/* Header — stays visible while the tracks scroll vertically in the dock */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/30 sticky top-0 z-20 bg-card rounded-t-lg">
        <div className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">Sequencer</span>
          <span className="text-[10px] text-muted-foreground">{tracks.length} clips · {totalDuration.toFixed(1)}s</span>
        </div>
        <div className="flex items-center gap-2">
          {hasSelection && (
            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={splitAtPlayhead} data-testid="split-btn">
              <Scissors className="h-3 w-3" /> Split
            </Button>
          )}
          <span className="text-[10px] font-mono text-red-400 tabular-nums w-12 text-right">{currentTime.toFixed(1)}s</span>
        </div>
      </div>

      {/* Multi-track area */}
      <div className="flex">
        {/* Track labels */}
        <div className="flex-shrink-0 border-r border-border/30" style={{ width: LABEL_W }}>
          <div className="flex items-end px-1.5 text-[7px] text-muted-foreground" style={{ height: RULER_H }} />
          <div className="flex items-center gap-1 px-1.5 border-t border-border/20" style={{ height: VIDEO_H }}>
            <Film className="h-3 w-3 text-purple-400" />
            <span className="text-[9px] text-muted-foreground">Video</span>
          </div>
          {layers.length > 0 && layers.map((layer, i) => (
            <div key={layer.id} className={`flex items-center gap-1 px-1.5 border-t border-border/20 cursor-pointer ${selectedLayerId === layer.id ? 'bg-violet-500/10' : 'hover:bg-muted/20'}`}
              style={{ height: LAYER_H }} onClick={() => onSelectLayer && onSelectLayer(layer.id)}>
              <span className="text-[8px] font-mono text-indigo-400 w-4">V{i + 1}</span>
            </div>
          ))}
          {showAudioTracks && (
            <>
              <div className="flex items-center gap-1 px-1.5 border-t border-border/20" style={{ height: AUDIO_H }}>
                <Music className="h-3 w-3 text-emerald-400" />
                <span className="text-[9px] text-muted-foreground">Music</span>
              </div>
              <div className="flex items-center gap-1 px-1.5 border-t border-border/20" style={{ height: AUDIO_H }}>
                <Mic className="h-3 w-3 text-amber-400" />
                <span className="text-[9px] text-muted-foreground">Voice</span>
              </div>
            </>
          )}
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto editor-scroll"
          style={{ scrollbarWidth: 'thin' }}
          onWheel={(e) => {
            // Shift + wheel scrolls the timeline horizontally (CapCut/Canva).
            if (e.shiftKey && e.deltaY !== 0) {
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
        >
          <div ref={contentRef} className="relative" style={{ minWidth: totalWidth }}>

            {/* Time ruler */}
            <div className="relative cursor-pointer select-none border-b border-border/30"
              style={{ height: RULER_H, touchAction: 'none' }} onClick={handleRulerClick}
              onPointerDown={(e) => { e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); handleRulerClick(e); setIsDraggingPlayhead(true); }}
              data-testid="timeline-ruler">
              {ticks}
            </div>

            {/* Video track */}
            <div className="relative border-b border-border/20" style={{ height: VIDEO_H, minWidth: totalWidth }}>
              <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                <div className="flex items-center h-full" style={{ minWidth: 'max-content' }}>
                  {tracks.length === 0 ? (
                    <div className="flex items-center gap-2 px-2">
                      <TimelineDropZone isOver={activeDropTarget === 'timeline-add-zone'} onAddBlank={onAddBlank} />
                      <span className="text-[10px] text-muted-foreground">Drag media here</span>
                    </div>
                  ) : (
                    <>
                      {tracks.map((track, idx) => (
                        <React.Fragment key={`${track.asset_id}-${idx}`}>
                          <DroppableClipWrapper clipIndex={idx}>
                            <SortableClip track={track} idx={idx} isSelected={selectedTrackIdx === idx}
                              isOver={activeDropTarget === `timeline-clip-${idx}`}
                              onClick={() => handleSelectTrack(idx)} />
                          </DroppableClipWrapper>
                          {idx < tracks.length - 1 && (
                            <div className="flex-shrink-0 flex items-center justify-center w-5 cursor-pointer group"
                              onClick={(e) => { e.stopPropagation(); handleSelectTrack(idx + 1); }}
                              title={TRANSITION_LABELS[tracks[idx + 1]?.transition] || 'Crossfade'}>
                              <div className="w-3.5 h-3.5 bg-card border border-border/60 rounded-sm rotate-45 flex items-center justify-center group-hover:border-purple-500/60 transition-colors">
                                <Zap className="h-1.5 w-1.5 text-muted-foreground -rotate-45" />
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                      <div className="ml-1"><TimelineDropZone isOver={activeDropTarget === 'timeline-add-zone'} onAddBlank={onAddBlank} /></div>
                    </>
                  )}
                </div>
              </SortableContext>
            </div>

            {/* Layer overlay tracks */}
            {layers.length > 0 && layers.map((layer, i) => (
              <div key={layer.id}
                className={`relative border-b border-border/20 ${selectedLayerId === layer.id ? 'bg-indigo-500/[0.06]' : 'bg-indigo-500/[0.02]'}`}
                style={{ height: LAYER_H, minWidth: totalWidth }}
                onClick={() => { onSelectLayer && onSelectLayer(layer.id); setSelectedTrackIdx(null); setSelectedAudioId(null); }}
                data-testid={`layer-track-${layer.id}`}
              >
                <LayerBlock
                  layer={layer}
                  totalDuration={totalDuration}
                  isSelected={selectedLayerId === layer.id}
                  onClick={() => { onSelectLayer && onSelectLayer(layer.id); setSelectedTrackIdx(null); setSelectedAudioId(null); }}
                  onUpdate={onLayerUpdate}
                />
              </div>
            ))}

            {/* Music track */}
            {showAudioTracks && (
              <div className="relative border-b border-border/20 bg-emerald-500/[0.03]" style={{ height: AUDIO_H, minWidth: totalWidth }}
                onClick={() => { setSelectedAudioId(null); setSelectedTrackIdx(null); }}>
                {musicSegs.map(seg => (
                  <AudioBlock key={seg.id} segment={seg} isSelected={selectedAudioId === seg.id}
                    onClick={() => handleSelectAudio(seg.id)} onPointerDown={(e) => handleAudioDragStart(e, seg)} />
                ))}
                {musicSegs.length === 0 && (
                  <div className="absolute inset-0 flex items-center px-2"><span className="text-[8px] text-muted-foreground/40">Upload music in the Audio panel</span></div>
                )}
              </div>
            )}

            {/* Voiceover track */}
            {showAudioTracks && (
              <div className="relative bg-amber-500/[0.03]" style={{ height: AUDIO_H, minWidth: totalWidth }}
                onClick={() => { setSelectedAudioId(null); setSelectedTrackIdx(null); }}>
                {voSegs.map(seg => (
                  <AudioBlock key={seg.id} segment={seg} isSelected={selectedAudioId === seg.id}
                    onClick={() => handleSelectAudio(seg.id)} onPointerDown={(e) => handleAudioDragStart(e, seg)} />
                ))}
                {voSegs.length === 0 && (
                  <div className="absolute inset-0 flex items-center px-2"><span className="text-[8px] text-muted-foreground/40">Generate voiceover in the Audio panel</span></div>
                )}
              </div>
            )}

            {/* Playhead — ref-driven for smooth 60fps updates */}
            {totalDuration > 0 && (
              <div ref={playheadRef} className="absolute z-30"
                style={{ left: cursorPx, top: 0, bottom: 0 }}
                data-testid="timeline-playhead">
                <div className="relative h-full">
                  {/* Triangle head — draggable */}
                  <div className="absolute -left-[7px] -top-[1px] cursor-col-resize"
                    style={{ width: 14, height: 10, pointerEvents: 'auto', touchAction: 'none' }}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); setIsDraggingPlayhead(true); if (onSeek) onSeek(getTimeFromX(e.clientX)); }}>
                    <svg width="14" height="10" viewBox="0 0 14 10">
                      <polygon points="0,0 14,0 7,10" fill="#ef4444" />
                    </svg>
                  </div>
                  {/* Line */}
                  <div className="w-0.5 h-full bg-red-500 mx-auto rounded-full shadow-sm shadow-red-500/40" />
                  {/* Wide invisible drag area */}
                  <div className="absolute -left-[8px] top-0 bottom-0 cursor-col-resize"
                    style={{ width: 16, pointerEvents: 'auto', touchAction: 'none' }}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); setIsDraggingPlayhead(true); if (onSeek) onSeek(getTimeFromX(e.clientX)); }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clip Detail Editor */}
      {selected && selectedTrackIdx !== null && (
        <ClipEditor
          track={selected}
          trackIdx={selectedTrackIdx}
          tracksCount={tracks.length}
          onUpdate={(u) => updateTrack(selectedTrackIdx, u)}
          onRemove={() => removeTrack(selectedTrackIdx)}
          onMove={(dir) => moveTrack(selectedTrackIdx, dir)}
        />
      )}

      {/* Audio Detail Editor */}
      {selectedAudio && (
        <AudioEditor
          segment={selectedAudio}
          totalDuration={totalDuration}
          onUpdate={(u) => updateAudioSeg(selectedAudio.id, u)}
          onRemove={() => removeAudioSeg(selectedAudio.id)}
        />
      )}
    </div>
  );
};
